"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { AuthDialog } from "../account/registration/AuthDialog";
import { createBrowserSupabaseClient } from "@/lib/supabase/clients/browser-client";

type AuthEvent = CustomEvent<{
  view?: "login" | "signup";
  email?: string;
  context?: "chat";
}>;

export function AuthDialogProvider() {
  const [isOpen, setIsOpen] = useState(false);
  const [initialEmail, setInitialEmail] = useState<string>();
  const [forceStep, setForceStep] = useState<"name_input" | undefined>();
  const [authContext, setAuthContext] = useState<"chat" | undefined>();
  const pathname = usePathname();
  // /editar is a token-gated owner surface — the app auth identity is irrelevant
  // there, so never force the blocking name-input modal on it.
  const onEditRoute = pathname?.endsWith("/editar") ?? false;

  // Listen for open-auth-dialog events
  useEffect(() => {
    const handleAuthEvent = (event: AuthEvent) => {
      setInitialEmail(event.detail.email);
      setAuthContext(event.detail.context);
      setForceStep(undefined);
      setIsOpen(true);
    };

    window.addEventListener("open-auth-dialog" as any, handleAuthEvent);
    return () => window.removeEventListener("open-auth-dialog" as any, handleAuthEvent);
  }, []);

  // After sign-in, check if display_name is missing → prompt for name
  useEffect(() => {
    const supabase = createBrowserSupabaseClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip anonymous users (#486): a Supabase anonymous sign-in fires
      // SIGNED_IN with no display_name, which would otherwise pop the
      // name-input modal on page load for a free-anon-chat visitor.
      if (event === "SIGNED_IN" && session?.user && !session.user.is_anonymous && !onEditRoute) {
        const displayName = session.user.user_metadata?.display_name;
        if (!displayName) {
          setForceStep("name_input");
          setIsOpen(true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [onEditRoute]);

  return (
    <AuthDialog
      open={isOpen}
      setOpen={setIsOpen}
      initialEmail={initialEmail}
      forceStep={forceStep}
      context={authContext}
    />
  );
}

// Helper function to trigger the modal from anywhere
// Keeps backward-compatible signature (view param accepted but ignored)
export function openAuthDialog(view: "login" | "signup" = "login", email?: string, context?: "chat") {
  const event = new CustomEvent("open-auth-dialog", {
    detail: { view, email, context },
  });
  window.dispatchEvent(event);
}
